import { useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Button } from "@similarweb/ui-components";
import { Box, Stack, Text } from "@similarweb/ui-components/foundations";

import { abbrNumberFilter } from "app/filters/ngFilters";

import { useAlertCounts } from "../tabs/useAlertCounts";

import { getExcelDataTypesConfig } from "./config";
import { ExportItem } from "./ExportItem";
import type { ExcelDataId } from "./types";

const getCountLabel = (count: number | undefined = 0, isLoading: boolean): string => {
    if (isLoading) {
        return "...";
    }

    return `${abbrNumberFilter()(count)}`;
};

type ExcelExportPopoverContentProps = {
    selectedIds: Set<ExcelDataId>;
    isAllSelected: boolean;
    isIndeterminate: boolean;
    isDownloading: boolean;
    toggleAll: () => void;
    toggleItem: (id: ExcelDataId) => void;
    onDownload: () => void;
};

export const ExcelExportPopoverContent = ({
    selectedIds,
    isAllSelected,
    isIndeterminate,
    isDownloading,
    toggleAll,
    toggleItem,
    onDownload,
}: ExcelExportPopoverContentProps) => {
    const { translate } = useTranslate();
    const { data: alertCounts, isLoading: isCountsLoading } = useAlertCounts();

    const dataTypesConfig = useMemo(() => getExcelDataTypesConfig(), []);

    const isDownloadDisabled = selectedIds.size === 0;

    const totalCount = useMemo(() => {
        if (!alertCounts) {
            return 0;
        }

        return dataTypesConfig.reduce((sum, dataType) => sum + (alertCounts[dataType.id] ?? 0), 0);
    }, [alertCounts, dataTypesConfig]);

    return (
        <Stack
            gap="24px"
            width="239px"
        >
            <Text
                fontVariant="h6"
                color={colorsPalettes.carbon[500]}
            >
                {translate("paid.excel.dialog.heading")}
            </Text>
            <Stack gap="16px">
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[500]}
                >
                    {translate("activation.alerts.excel-popup.subtitle")}
                </Text>
                <Stack gap="8px">
                    <ExportItem
                        countLabel={getCountLabel(totalCount, isCountsLoading)}
                        dataAutomation="excel-popup-option-select-all"
                        halfSelected={isIndeterminate}
                        isDisabled={isDownloading}
                        label={translate("activation.alerts.excel-popup.option.allTypes")}
                        onClick={toggleAll}
                        selected={isAllSelected}
                    />

                    <Box
                        height="1px"
                        background={colorsPalettes.carbon[50]}
                    />

                    {dataTypesConfig.map(({ id, translationKey }) => (
                        <ExportItem
                            key={id}
                            countLabel={getCountLabel(alertCounts?.[id], isCountsLoading)}
                            dataAutomation={`excel-popup-option-${id}`}
                            isDisabled={isDownloading}
                            label={translate(translationKey)}
                            onClick={() => toggleItem(id)}
                            selected={selectedIds.has(id)}
                        />
                    ))}
                </Stack>
            </Stack>
            <Button
                type="primary"
                onClick={onDownload}
                isDisabled={isDownloadDisabled || isDownloading}
                isLoading={isDownloading}
                width="100%"
                data-automation="alerts-excel-download-button"
            >
                {translate("btn.download")}
            </Button>
        </Stack>
    );
};
